import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatFullCurrency, formatPercent, toNumber } from "../../utils/format";

/**
 * PdfExportButton. Renders "Download PDF" for the Executive Summary.
 *
 * Approach: text-based jsPDF + jspdf-autotable. No raster snapshot.
 *   - Branded cover page (logo + report title + period).
 *   - Numbered pages, footer with generation timestamp on every page.
 *   - Full precise values (never abbreviated) per Section 6 of project-brief-v1.
 *   - Negatives in parentheses per Section 6.2 (mode "export").
 *   - Result: searchable PDF under ~200 KB typical.
 *
 * Data flows through the `payload` prop instead of DOM cloning:
 *   payload = {
 *     company: string,
 *     period: string,
 *     balances: [{ label, value, previous }],
 *     revenue: [{ label, value }],           // 5 KPI cards
 *     pnl: [{ label, value, pct }],
 *     balanceSheet: [{ label, value }],
 *     arAging: [{ bucket, value }],
 *     apAging: [{ bucket, value }],
 *   }
 */
export default function PdfExportButton({ payload, filename = "onpoint-summary", testid = "pdf-export-button" }) {
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const now = new Date();
      const generated = now.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
      const isoDate = now.toISOString().slice(0, 10);

      drawCoverPage(doc, payload, generated);
      doc.addPage();
      if (payload?.sections && payload.sections.length) {
        drawGenericSections(doc, payload);
      } else {
        drawExecutiveSummaryPages(doc, payload, generated);
      }
      addFooters(doc, generated);

      doc.save(`${filename}-${isoDate}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={busy || !payload}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        backgroundColor: busy || !payload ? "#E8EAF0" : "#1B75BA",
        color: busy || !payload ? "#6B7491" : "#FFFFFF",
        border: "1px solid #1B75BA",
        cursor: busy ? "wait" : (payload ? "pointer" : "not-allowed"),
      }}
      data-testid={testid}
      aria-label="Download this dashboard as a PDF"
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
      <span>{busy ? "Preparing..." : "Download PDF"}</span>
    </button>
  );
}

/**
 * Branded cover page. Logo bar + big title + period + generated line.
 * Colors match the OnPoint brand blue and ink.
 */
function drawCoverPage(doc, payload, generated) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Blue brand band across the top.
  doc.setFillColor(27, 117, 186); // #1B75BA
  doc.rect(0, 0, pageW, 42, "F");

  // Brand mark.
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("OnPoint", 20, 24);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("BUSINESS INTELLIGENCE", 20, 32);

  // Report title.
  doc.setTextColor(28, 32, 64); // #1C2040
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text(payload?.title || "Executive Summary", 20, 84);

  // Subtitle: company + period.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(14);
  doc.setTextColor(107, 116, 145); // #6B7491
  doc.text(payload?.company || "Business Report", 20, 96);
  doc.setFontSize(12);
  doc.text(`Reporting Period: ${payload?.period || "Not specified"}`, 20, 106);
  doc.text(`Generated: ${generated}`, 20, 114);

  // Divider.
  doc.setDrawColor(232, 234, 240); // #E8EAF0
  doc.setLineWidth(0.3);
  doc.line(20, 124, pageW - 20, 124);

  // Table of contents.
  doc.setTextColor(28, 32, 64);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("What is in this report", 20, 138);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 112); // #4B5570
  const toc = payload?.sections
    ? payload.sections.map((s, idx) => `${idx + 1}. ${s.title}`)
    : [
        "1. Cash and Receivables balances (MTD)",
        "2. Revenue Performance KPIs",
        "3. Profit and Loss summary",
        "4. Balance Sheet summary",
        "5. Accounts Receivable aging",
        "6. Accounts Payable aging",
      ];
  toc.forEach((line, idx) => {
    doc.text(line, 24, 148 + idx * 7);
  });

  // Compliance note.
  doc.setTextColor(155, 164, 184); // #9BA4B8
  doc.setFontSize(9);
  const noteLines = [
    "All monetary values are shown at full precision without abbreviation.",
    "Negative values are shown in accounting parentheses. Example: ($1,234.56).",
    "Data source: QuickBooks (accounting).",
  ];
  noteLines.forEach((line, idx) => {
    doc.text(line, 20, pageH - 30 + idx * 5);
  });
}

/**
 * Content pages. Section headers + text-based tables via jspdf-autotable.
 * Every currency value uses `formatFullCurrency(v, { mode: "export" })` so
 * negatives are wrapped in parentheses (Section 6.2).
 */
function drawExecutiveSummaryPages(doc, payload, generated) {
  const pageW = doc.internal.pageSize.getWidth();
  let cursorY = 20;

  const sectionTitle = (title) => {
    doc.setTextColor(28, 32, 64);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, 14, cursorY);
    doc.setDrawColor(27, 117, 186);
    doc.setLineWidth(0.6);
    doc.line(14, cursorY + 1.5, 14 + doc.getTextWidth(title) + 4, cursorY + 1.5);
    cursorY += 6;
  };

  const drawTable = (head, body, subtotalIndices = []) => {
    autoTable(doc, {
      head: [head],
      body,
      startY: cursorY,
      margin: { left: 14, right: 14 },
      theme: "striped",
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, textColor: [28, 32, 64] },
      headStyles: { fillColor: [27, 117, 186], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [249, 250, 252] },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "right", cellWidth: 42, font: "courier" },
        2: { halign: "right", cellWidth: 32, font: "courier" },
      },
      didParseCell: (data) => {
        if (subtotalIndices.includes(data.row.index)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [239, 246, 255];
        }
      },
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  };

  const ensureRoom = (needed = 40) => {
    const pageH = doc.internal.pageSize.getHeight();
    if (cursorY + needed > pageH - 20) {
      doc.addPage();
      cursorY = 20;
    }
  };

  // Section 1: Cash & Receivables.
  ensureRoom(60);
  sectionTitle("1. Cash and Receivables (MTD)");
  drawTable(
    ["Account", "Balance", "Previous"],
    (payload?.balances || []).map((r) => [
      r.label,
      formatFullCurrency(r.value, { mode: "export" }),
      formatFullCurrency(r.previous, { mode: "export" }),
    ]),
  );

  // Section 2: Revenue Performance.
  ensureRoom(50);
  sectionTitle("2. Revenue Performance");
  drawTable(
    ["Metric", "Value"],
    (payload?.revenue || []).map((r) => [
      r.label,
      typeof r.value === "number" ? formatFullCurrency(r.value, { mode: "export" }) : String(r.value ?? "--"),
    ]),
  );

  // Section 3: P&L Summary.
  ensureRoom(80);
  sectionTitle("3. Profit and Loss (Summary)");
  const pnlRows = (payload?.pnl || []).map((r) => [
    r.label,
    formatFullCurrency(r.value, { mode: "export" }),
    r.pct !== undefined && r.pct !== null ? formatPercent(r.pct, { decimals: 1 }) : "--",
  ]);
  drawTable(["Line item", "Amount", "% of Revenue"], pnlRows, (payload?.pnlSubtotalIndices) || []);

  // Section 4: Balance Sheet Summary.
  ensureRoom(80);
  sectionTitle("4. Balance Sheet (Summary)");
  drawTable(
    ["Line item", "Amount"],
    (payload?.balanceSheet || []).map((r) => [
      r.label,
      formatFullCurrency(r.value, { mode: "export" }),
    ]),
    (payload?.bsSubtotalIndices) || [],
  );

  // Section 5: AR Aging (always render for TOC consistency).
  ensureRoom(60);
  sectionTitle("5. Accounts Receivable Aging");
  if (payload?.arAging && payload.arAging.length) {
    drawTable(
      ["Bucket", "Amount"],
      payload.arAging.map((r) => [r.bucket, formatFullCurrency(r.value, { mode: "export" })]),
    );
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(107, 116, 145);
    doc.text("Aging detail not available for the selected period.", 14, cursorY);
    cursorY += 12;
  }

  // Section 6: AP Aging (always render for TOC consistency).
  ensureRoom(60);
  sectionTitle("6. Accounts Payable Aging");
  if (payload?.apAging && payload.apAging.length) {
    drawTable(
      ["Bucket", "Amount"],
      payload.apAging.map((r) => [r.bucket, formatFullCurrency(r.value, { mode: "export" })]),
    );
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(107, 116, 145);
    doc.text("Aging detail not available for the selected period.", 14, cursorY);
    cursorY += 12;
  }
}

/**
 * Generic sections renderer for non-Executive-Summary tabs.
 *
 * payload.sections is a list of { title, columns, rows } blocks:
 *   { title: "Profit & Loss Summary",
 *     columns: [{ label: "Line item", key: "label" },
 *               { label: "Amount",    key: "value", format: "currency", align: "right" },
 *               { label: "% Revenue", key: "pct",   format: "percent",  align: "right" }],
 *     rows: [{ label: "Revenue", value: 539000, pct: 100 }, ...],
 *     subtotalIndices: [0, 3]
 *   }
 * or a note block: { title, note: "..." }
 */
function drawGenericSections(doc, payload) {
  let cursorY = 20;

  const ensureRoom = (needed = 40) => {
    const pageH = doc.internal.pageSize.getHeight();
    if (cursorY + needed > pageH - 20) {
      doc.addPage();
      cursorY = 20;
    }
  };

  const sectionTitle = (title, index) => {
    doc.setTextColor(28, 32, 64);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    const heading = `${index + 1}. ${title}`;
    doc.text(heading, 14, cursorY);
    doc.setDrawColor(27, 117, 186);
    doc.setLineWidth(0.6);
    doc.line(14, cursorY + 1.5, 14 + doc.getTextWidth(heading) + 4, cursorY + 1.5);
    cursorY += 6;
  };

  const formatCell = (v, fmt) => {
    if (v === null || v === undefined || v === "") return "--";
    if (fmt === "currency") return formatFullCurrency(v, { mode: "export" });
    if (fmt === "percent") return typeof v === "number" ? formatPercent(v, { decimals: 1 }) : String(v);
    if (fmt === "integer") return typeof v === "number" ? new Intl.NumberFormat("en-US").format(v) : String(v);
    return String(v);
  };

  payload.sections.forEach((section, idx) => {
    ensureRoom(60);
    sectionTitle(section.title, idx);

    if (section.note && !section.rows?.length) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(107, 116, 145);
      const lines = doc.splitTextToSize(section.note, doc.internal.pageSize.getWidth() - 28);
      lines.forEach((ln) => { doc.text(ln, 14, cursorY); cursorY += 5; });
      cursorY += 4;
      return;
    }

    const columns = section.columns || [
      { label: "Metric", key: "label" },
      { label: "Value", key: "value", format: "currency", align: "right" },
    ];
    const head = [columns.map((c) => c.label)];
    const body = (section.rows || []).map((row) =>
      columns.map((c) => {
        const raw = row[c.key];
        const cell = formatCell(raw, c.format);
        return cell;
      }),
    );
    const columnStyles = {};
    columns.forEach((c, i) => {
      const style = {};
      if (c.align === "right") style.halign = "right";
      if (c.format === "currency" || c.format === "percent") style.font = "courier";
      if (c.width) style.cellWidth = c.width;
      else if (c.align === "right") style.cellWidth = 40;
      else style.cellWidth = "auto";
      columnStyles[i] = style;
    });

    autoTable(doc, {
      head,
      body,
      startY: cursorY,
      margin: { left: 14, right: 14 },
      theme: "striped",
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, textColor: [28, 32, 64] },
      headStyles: { fillColor: [27, 117, 186], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [249, 250, 252] },
      columnStyles,
      didParseCell: (data) => {
        if ((section.subtotalIndices || []).includes(data.row.index)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [239, 246, 255];
        }
      },
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  });
}

/**
 * Adds a page number and generation timestamp footer to every page.
 */
function addFooters(doc, generated) {
  const total = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(155, 164, 184);
    doc.text(`OnPoint BI. Generated ${generated}.`, 14, pageH - 8);
    doc.text(`Page ${i} of ${total}`, pageW - 14, pageH - 8, { align: "right" });
  }
}

/**
 * Static helper. Format a number for a text-based export cell.
 * Currency always fully expressed. Negatives in parentheses.
 */
export function formatForExport(value) {
  const n = toNumber(value);
  return formatFullCurrency(n, { mode: "export" });
}
